import { useLookup, useWorkspace } from "@/hooks";
import {
  Editor,
  EditorMentionItemType,
  EditorFileUploadResponseType,
  EditorRefType,
  EditorSubmitPayloadType,
} from "@ir/ui";
import { memo, RefObject, useCallback, useEffect, useRef, useState } from "react";
import { useDeductionDrawer } from "@/presentation/deduction/hooks";
import { ECurrentMessageReceiveAction, SendMessageOptions } from "./interface/InternalConversationContextType";
import { ConversationMessage } from "@/core/entities/conversation";
import { Conversation } from "@/core/entities/conversation/Conversation";
import { IUploadAttachmentResponse } from "@/core/repositories/conversation";

const editorConfig = {
  maxFileUpload: 5,
  maxFileSize: 15,
  acceptFiletype: ["pdf", "xlsx", "xls", "csv", "txt", "doc", "docx", "jpg", "png", "gif", "tiff", "bmp", "edi"],
  minHeight: 30,
  maxHeight: 80,
  maxCharacterLimit: 2000,
};

interface InternalConversationPostMessageProps {
  setCurrentMessageReceiveAction: (action: ECurrentMessageReceiveAction) => void;
  onMessageSent?: (message: ConversationMessage) => void;
  sendMessage: (
    data: EditorSubmitPayloadType,
    invoiceNumber: string,
    options?: SendMessageOptions
  ) => Promise<{ action: ECurrentMessageReceiveAction; message: ConversationMessage | null }>;
  uploadConversationAttachment: (file: File) => Promise<IUploadAttachmentResponse>;
  conversation: Conversation | null;
}

const InternalConversationPostMessage = ({
  setCurrentMessageReceiveAction,
  onMessageSent,
  sendMessage,
  uploadConversationAttachment,
  conversation,
}: InternalConversationPostMessageProps) => {
  const editorRef = useRef<EditorRefType | null>(null);
  const { drawerInfo } = useDeductionDrawer();
  const { brandMembersLookup, getBrandMembersLookup } = useLookup();
  const { workspace } = useWorkspace();
  const [brandMemberList, setBrandMemberList] = useState<EditorMentionItemType[]>([]);

  // Reset editor when drawer changes - using key
  useEffect(() => {
    setTimeout(() => {
      if (editorRef.current) {
        editorRef.current.resetEditor();
        editorRef.current.focusEditor();
      }
    }, 500);
  }, [drawerInfo?.invoiceNumber, editorRef]);

  const handleSendMessage = useCallback(
    async (data: EditorSubmitPayloadType) => {
      setCurrentMessageReceiveAction(ECurrentMessageReceiveAction.INITIAL_LOAD);
      if (!drawerInfo?.invoiceNumber) return;
      const { action, message } = await sendMessage(data, drawerInfo.invoiceNumber, {
        onOptimisticMessage: onMessageSent,
      });
      setCurrentMessageReceiveAction(action);
      if (message) {
        onMessageSent?.(message);
      }
      setTimeout(() => {
        if (editorRef.current) {
          editorRef.current.focusEditor();
        }
      }, 10);
    },
    [sendMessage, drawerInfo, editorRef, onMessageSent, setCurrentMessageReceiveAction]
  );

  const handleFileUpload = async (attachmentFile: File): Promise<EditorFileUploadResponseType> => {
    const uploadedDocument = await uploadConversationAttachment(attachmentFile);
    return uploadedDocument;
  };

  useEffect(() => {
    getBrandMembersLookup({  include_deleted: true, include_bots: false, role_key: [] });
  }, [workspace, getBrandMembersLookup]);

  useEffect(() => {
    setTimeout(() => {
      setBrandMemberList([]);
    }, 10);
    if (brandMembersLookup.data) {
      const filteredBrandMembers = brandMembersLookup.data.filter(member => !member.isDeleted);
      if (filteredBrandMembers.length > 0) {
        setTimeout(() => {
          for (const { userId, fullName } of filteredBrandMembers) {
            setBrandMemberList(prev => [...prev, { id: userId, value: fullName }]);
          }
        }, 10);
      }
    }
  }, [brandMembersLookup.data, setBrandMemberList]);

  return (
    <Editor
      key={drawerInfo?.invoiceNumber}
      onSubmit={handleSendMessage}
      atMentions={brandMemberList}
      maxFileUpload={editorConfig.maxFileUpload}
      maxFileSize={editorConfig.maxFileSize}
      acceptFiletype={editorConfig.acceptFiletype}
      minHeight={editorConfig.minHeight}
      maxHeight={editorConfig.maxHeight}
      maxCharacterLimit={editorConfig.maxCharacterLimit}
      onFileUpload={handleFileUpload}
      isdisabled={!conversation?.id}
      editorRef={editorRef as RefObject<EditorRefType>}
    />
  );
};

export default memo(InternalConversationPostMessage);
